/**
 * Shared color scheme type for EagGrid filter category UI.
 *
 * This type is intentionally extracted here so every component that needs to
 * express a category color — `ActiveFilterChip` (Tag colorScheme),
 * `FilterChipPopover` (overflow dropdown) — imports from this single source of truth.
 *
 * Benefits:
 * - **Single place to add/remove colors**: drop a value here and TypeScript will
 *   flag every callsite that needs updating.
 * - **Configurable palette**: to restrict or expand the colour cycle, only
 *   `COLOR_CYCLE` needs to change — all consumers automatically follow.
 * - **Consistent semantics**: `Badge`, `Tag`, and dot indicators all accept the
 *   same `colorScheme` union from platform/components; keeping one shared type
 *   prevents the union from drifting out of sync across files.
 */
export type CategoryColorScheme =
  'blue' | 'orange' | 'green' | 'purple' | 'teal' | 'red' | 'magenta' | 'yellow' | 'neutral';

const CATEGORY_COLOR_MAP: Record<string, CategoryColorScheme> = {};

let colorIndex = 0;

const COLOR_CYCLE: CategoryColorScheme[] = [
  'blue',
  'orange',
  'green',
  'purple',
  'teal',
  'red',
  'magenta',
  'yellow',
];

/**
 * Returns a consistent color scheme for a given category groupId.
 *
 * Assigns colors in order of first encounter, cycling through `COLOR_CYCLE`.
 * Standalone filters (no group, i.e. `groupId` is `undefined`) always receive
 * `'neutral'`.
 *
 * @param groupId - The `groupId` from `EagColumnGroup`, or `undefined` for
 *   standalone columns that do not belong to any category.
 */
export const getCategoryColor = (groupId: string | undefined): CategoryColorScheme => {
  if (!groupId) {
    return 'neutral';
  }

  if (!CATEGORY_COLOR_MAP[groupId]) {
    CATEGORY_COLOR_MAP[groupId] = COLOR_CYCLE[colorIndex % COLOR_CYCLE.length];
    colorIndex++;
  }

  return CATEGORY_COLOR_MAP[groupId];
};
